import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {AlertListService} from '@xref/ngx';

import {ElementHelperService} from '../../../../services/element-helper/element-helper.service';
import {PermissionsService} from '../../../../services/permissions/permissions.service';
import {TokenService} from '../../../../services/token/token.service';
import {CHALLENGE_TYPE, MFA_FEATURE_FLAG, TwoFactorService} from '../../../../services/two-factor/two-factor.service';
import {IdService} from '../../../../services/id/id.service';
import {SignupMfaLockoutService} from '../../../../services/signup-mfa-lockout/signup-mfa-lockout.service';

const OTP_SEND_LIMIT_REACHED = 'MOBILE_OTP_SEND_LIMIT_REACHED';
const OTP_CHECK_LIMIT_REACHED = 'MOBILE_OTP_CHECK_LIMIT_REACHED';

@Component({
    selector: 'xlog-two-factor-verification',
    templateUrl: './two-factor-verification.component.pug',
    styleUrls: ['./two-factor-verification.component.scss'],
    standalone: false
})
export class TwoFactorVerificationComponent implements OnInit, AfterViewInit {
    @ViewChild('formRef', {static: true}) form: any;

    @Input() isSetup: boolean;
    @Input() verificationType: string;

    @Output() isSetupUpdated = new EventEmitter();

    readonly CHALLENGE_TYPE = CHALLENGE_TYPE;

    loaded = false;
    verifyModel = { mfa_code: ''};

    submitted: boolean;
    submitting: boolean;
    codeError = false;
    otpSendLimitReached = false;

    constructor(
        private alertList: AlertListService,
        private router: Router,
        public twoFactor: TwoFactorService,
        private token: TokenService,
        private el: ElementHelperService,
        private id: IdService,
        private signupMfaLockout: SignupMfaLockoutService,
        private permissions: PermissionsService,
    ) {}

    ngOnInit() {
        if (this.isMfaFeatureOn()) {
            // Enterprise authenticator-app challenges do not require an OTP to be
            // dispatched — the code is generated by the user's app — so we skip the
            // request step and let them enter the code immediately.
            if (this.twoFactor.data.mfa_enabled && !this.isAppChallenge()) {
                this.requestVerificationCode();
            } else {
                this.loaded = true;
            }
        } else if (this.twoFactor.data.mfa_enabled) {
            this.requestVerificationCode();
        } else {
            this.loaded = true;
        }
    }

    ngAfterViewInit() {
        this.el.focusById('code');
    }

    isMfaFeatureOn(): boolean {
        return this.permissions.isFeatureActivated(MFA_FEATURE_FLAG);
    }

    /**
     * True when the configured login-time method is the Enterprise authenticator app.
     */
    isAppChallenge(): boolean {
        return this.isMfaFeatureOn()
            && this.twoFactor.isEnterprise()
            && this.twoFactor.data.challenge_type === CHALLENGE_TYPE.APP;
    }

    /**
     * True when the configured login-time method is Enterprise SMS. Enterprise
     * carries the method on `challenge_type`, not the legacy `user_choice`.
     */
    isSmsChallenge(): boolean {
        return this.isMfaFeatureOn()
            && this.twoFactor.isEnterprise()
            && this.twoFactor.data.challenge_type === CHALLENGE_TYPE.SMS;
    }

    /**
   * Verify a 2FA code. If valid store JWT in cookie and redirect user. Otherwise show
   * correct error message.
   */
    verifyVerificationCode() {
        this.submitted = true;

        if (this.form.invalid) return;

        this.submitting = true;
        this.codeError = false;

        const success = (res: any) => {
            this.id.completeMfaLogin(res.data);
        };

        const error = (err: any) => {
            if (this.isMfaFeatureOn()) {
                this.handleVerifyErrorWithMfa(err);
            } else {
                this.handleVerifyErrorLegacy(err);
            }
            this.submitting = false;
        };

        if (this.twoFactor.data.mfa_enabled) {
            this.twoFactor
                .verifyCode(this.token.data.token, this.verifyModel)
                .subscribe(success, error);
        } else if (this.twoFactor.data.user_choice === 'email') {
            this.twoFactor
                .verifyEmailVerificationCode(this.token.data.token, this.verifyModel)
                .subscribe(success, error);
        } else if (this.twoFactor.data.user_choice === 'sms') {
            this.twoFactor
                .verifySmsVerificationCode(this.token.data.token, this.verifyModel)
                .subscribe(success, error);
        }
    }

    /**
   * Request a 2FA code.
   */
    requestVerificationCode() {
        if (this.isMfaFeatureOn() && this.otpSendLimitReached) {
            return;
        }

        this.submitting = true;

        // Reset form model
        this.verifyModel.mfa_code = '';
        this.submitted = false;
        this.codeError = false;

        const success = () => {
            if (this.loaded) this.alertList.append([{active: true, type: 'success', messageKey: 'twoFactor.alerts.codeResent'}]);

            this.otpSendLimitReached = false;
            this.submitting = false;
            this.loaded = true;
        };

        const error = (err: any) => {
            if (this.isMfaFeatureOn()) {
                this.handleRequestErrorWithMfa(err);
            } else {
                this.handleRequestErrorLegacy(err);
            }
            this.submitting = false;
            this.loaded = true;
        };

        if (this.twoFactor.data.mfa_enabled) {
            this.twoFactor
                .requestCode(this.token.data.token)
                .subscribe(success, error);
        } else if (this.twoFactor.data.user_choice === 'email') {
            this.twoFactor
                .requestEmailVerificationCode(this.token.data.token)
                .subscribe(success, error);
        } else if (this.twoFactor.data.user_choice === 'sms') {
            this.twoFactor
                .requestSmsVerificationCode(this.token.data.token, this.twoFactor.smsModel)
                .subscribe(success, error);
        }
    }

    /**
   * Reset UI to display the setup component.
   */
    returnToSetup() {
        this.isSetup = false;
        this.isSetupUpdated.next(this.isSetup);
    }

    private handleVerifyErrorWithMfa(err: any) {
        if (this.signupMfaLockout.handleIfSignupMfaExhausted(err)) {
            return;
        }

        const otpErrorCode = this.getOtpErrorCode(err);
        const errorBody = err?.error;

        if (otpErrorCode === OTP_CHECK_LIMIT_REACHED) {
            this.alertList.append([{active: true, type: 'danger', messageKey: 'twoFactor.alerts.otpCheckLimitReached'}]);
        } else if (errorBody?.hasOwnProperty('mfa_code')) {
            this.codeError = true;
        } else if (errorBody?.detail === 'User account is disabled.') {
            this.alertList.append([{active: true, type: 'danger', messageKey: `alerts.auth.User account is disabled.`, keepOnRouteChange: true}]);
            this.router.navigate(['/']);
        } else if (errorBody?.detail === 'Signature has expired.') {
            this.codeError = true;
        } else {
            this.alertList.append([{active: true, type: 'danger', messageKey: 'alerts.generic.error'}]);
        }
    }

    private handleVerifyErrorLegacy(err: any) {
        const errorBody = err?.error;

        if (errorBody?.hasOwnProperty('mfa_code')) {
            this.alertList.append([{active: true, type: 'danger', messageKey: 'twoFactor.alerts.invalidCode'}]);
        } else if (errorBody?.detail === 'User account is disabled.') {
            this.alertList.append([{active: true, type: 'danger', messageKey: `alerts.auth.User account is disabled.`, keepOnRouteChange: true}]);
            this.router.navigate(['/']);
        } else if (errorBody?.detail === 'Signature has expired.') {
            this.alertList.append([{active: true, type: 'danger', messageKey: 'twoFactor.alerts.invalidCode'}]);
        } else {
            this.alertList.append([{active: true, type: 'danger', messageKey: 'alerts.generic.error'}]);
        }
    }

    private handleRequestErrorWithMfa(err: any) {
        const otpErrorCode = this.getOtpErrorCode(err);
        const errorBody = err?.error;

        if (otpErrorCode === OTP_SEND_LIMIT_REACHED) {
            this.otpSendLimitReached = true;
            this.alertList.append([{active: true, type: 'danger', messageKey: 'twoFactor.alerts.otpSendLimitReached'}]);
        } else if (errorBody?.hasOwnProperty('phone_number')) {
            this.alertList.append([{active: true, type: 'danger', messageKey: 'twoFactor.alerts.invalidPhoneNumber'}]);
        } else if (errorBody?.detail === 'User account is disabled.') {
            this.alertList.append([{active: true, type: 'danger', messageKey: `alerts.auth.User account is disabled.`, keepOnRouteChange: true}]);
            this.router.navigate(['/']);
        } else {
            this.alertList.append([{active: true, type: 'danger', messageKey: 'alerts.generic.error'}]);
        }
    }

    private handleRequestErrorLegacy(err: any) {
        const errorBody = err?.error;

        if (errorBody?.hasOwnProperty('phone_number')) {
            this.alertList.append([{active: true, type: 'danger', messageKey: 'twoFactor.alerts.invalidPhoneNumber'}]);
        } else if (errorBody?.detail === 'User account is disabled.') {
            this.alertList.append([{active: true, type: 'danger', messageKey: `alerts.auth.User account is disabled.`, keepOnRouteChange: true}]);
            this.router.navigate(['/']);
        } else {
            this.alertList.append([{active: true, type: 'danger', messageKey: 'alerts.generic.error'}]);
        }
    }

    /**
     * Read the domain OTP error code from an OmniAppResponse-shaped HTTP error body.
     */
    private getOtpErrorCode(err: any): string | null {
        if (!err || !err.error || !err.error.data || !err.error.data.details) {
            return null;
        }

        return err.error.data.details.code || null;
    }
}
